import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { GET_INSTANCES_BY_AMIS } from '../graphql/queries';
import { DataTable } from '../components/DataTable';

const stateColors = {
  running: '#00b894',
  stopped: '#fdcb6e',
  terminated: '#d63031',
};

const instanceColumns = [
  {
    accessorKey: 'instanceId',
    header: 'Instance ID',
    cell: ({ getValue }) => <code style={{ fontSize: 12 }}>{getValue()}</code>,
  },
  { accessorKey: 'instanceType', header: 'Type' },
  {
    accessorKey: 'state',
    header: 'State',
    cell: ({ getValue }) => (
      <span style={{
        color: stateColors[getValue()] || '#636e72',
        fontWeight: 600,
      }}>
        {getValue()}
      </span>
    ),
  },
  {
    accessorKey: 'launchTime',
    header: 'Launched',
    cell: ({ getValue }) => {
      const d = new Date(getValue());
      const days = Math.floor((Date.now() - d.getTime()) / 86400000);
      return (
        <span title={d.toISOString()}>
          {d.toLocaleDateString()} <span style={{ color: '#636e72', fontSize: 11 }}>({days}d ago)</span>
        </span>
      );
    },
  },
  {
    accessorKey: 'tags',
    header: 'Name',
    cell: ({ getValue }) => {
      const name = getValue()?.find(t => t.key === 'Name');
      return name ? name.value : '-';
    },
  },
];

function StatCard({ label, value, color }) {
  return (
    <div className="stat-card" style={{ borderLeft: `4px solid ${color}` }}>
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

export default function FleetInstances() {
  const [searchParams] = useSearchParams();
  const initialAmiIds = searchParams.get('amiIds')?.split(',').filter(Boolean) || [];
  const [amiInput, setAmiInput] = useState(initialAmiIds.join('\n'));
  const [fetchInstances, { data, loading, error }] = useLazyQuery(GET_INSTANCES_BY_AMIS);

  const handleSearch = () => {
    const amiIds = amiInput
      .split(/[\n,]+/)
      .map(id => id.trim())
      .filter(id => id.startsWith('ami-'));
    if (amiIds.length > 0) {
      fetchInstances({ variables: { amiIds } });
    }
  };

  // Auto-fetch if AMI IDs provided via URL
  React.useEffect(() => {
    if (initialAmiIds.length > 0) {
      fetchInstances({ variables: { amiIds: initialAmiIds } });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const result = data?.getInstancesByAMIs;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <Link to="/fleet" style={{ color: '#0984e3', textDecoration: 'none' }}>Fleet</Link>
        <span style={{ color: '#636e72' }}>/</span>
        <h2 className="page-title" style={{ margin: 0 }}>Instance Lookup</h2>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginBottom: 8 }}>AMI ID Lookup</h3>
        <p style={{ color: '#636e72', fontSize: 13, marginBottom: 8 }}>
          Enter one or more AMI IDs to find all deployed instances across regions and accounts.
        </p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <textarea
            value={amiInput}
            onChange={e => setAmiInput(e.target.value)}
            placeholder="ami-0123456789abcdef0&#10;ami-0987654321fedcba0&#10;..."
            rows={4}
            style={{
              flex: 1,
              fontFamily: 'monospace',
              fontSize: 13,
              padding: 10,
              borderRadius: 6,
              border: '1px solid var(--border-color, #ddd)',
              background: 'var(--input-bg, #fff)',
              color: 'var(--text-primary, #2d3436)',
              resize: 'vertical',
            }}
          />
          <button
            onClick={handleSearch}
            disabled={loading}
            style={{
              padding: '10px 24px',
              background: '#0984e3',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: loading ? 'wait' : 'pointer',
              fontWeight: 600,
            }}
          >
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
      </div>

      {error && (
        <div className="card" style={{ borderLeft: '4px solid #d63031', marginBottom: 16 }}>
          <p style={{ color: '#d63031' }}>Error: {error.message}</p>
        </div>
      )}

      {result && (
        <>
          <div className="stats-grid" style={{ marginBottom: 16 }}>
            <StatCard label="Total Instances" value={result.totalInstances} color="#0984e3" />
            <StatCard label="AMIs Matched" value={result.totalAMIs} color="#6c5ce7" />
            <StatCard label="Regions" value={result.totalRegions} color="#00b894" />
          </div>

          {result.groups.map(group => (
            <div key={group.amiId} className="card" style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h3 style={{ margin: 0 }}>
                  <code style={{ fontSize: 14 }}>{group.amiId}</code>
                </h3>
                <span style={{
                  background: '#0984e3',
                  color: '#fff',
                  padding: '2px 10px',
                  borderRadius: 12,
                  fontSize: 13,
                  fontWeight: 600,
                }}>
                  {group.totalInstances} instance{group.totalInstances !== 1 ? 's' : ''}
                </span>
              </div>

              {group.regions.map(regionGroup => (
                <div key={regionGroup.region} style={{ marginBottom: 16 }}>
                  {regionGroup.accounts.map(accountGroup => (
                    <div key={accountGroup.accountId} style={{ marginBottom: 12 }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        marginBottom: 8,
                        padding: '6px 12px',
                        background: 'var(--bg-secondary, #f5f6fa)',
                        borderRadius: 6,
                      }}>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>
                          {regionGroup.region}
                        </span>
                        <span style={{ color: '#636e72', fontSize: 12 }}>|</span>
                        <span style={{ fontSize: 13, color: '#636e72' }}>
                          Account: <code>{accountGroup.accountId}</code>
                        </span>
                        <span style={{
                          marginLeft: 'auto',
                          background: '#dfe6e9',
                          padding: '1px 8px',
                          borderRadius: 10,
                          fontSize: 12,
                          fontWeight: 600,
                        }}>
                          {accountGroup.instanceCount}
                        </span>
                      </div>
                      <DataTable
                        columns={instanceColumns}
                        data={accountGroup.instances}
                        pageSize={10}
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))}

          {result.totalInstances === 0 && (
            <div className="card" style={{ textAlign: 'center', padding: 32, color: '#636e72' }}>
              No instances found for the provided AMI IDs.
            </div>
          )}
        </>
      )}
    </div>
  );
}
